import { Injectable } from '@angular/core';
import { DateRangeOption, DateRangeSelection } from '../models/date-range.model';

/**
 * Service for handling date range calculations and formatting.
 * Provides reusable logic for date filtering across components.
 */
@Injectable({
  providedIn: 'root'
})
export class DateRangeService {

  /**
   * Calculates the date range based on the selected option
   * @param option - The date range option selected
   * @param days - Optional number of days (used for specific day ranges)
   * @returns Tuple with [startDate, endDate]
   */
  calculateDateRange(option: DateRangeOption, days?: number | null): [Date, Date] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (days !== null && days !== undefined) {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - days);
      return [startDate, today];
    }

    switch (option) {
      case 'today':
        return [new Date(today), new Date(today)];

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return [yesterday, yesterday];
      }

      case 'last7days': {
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        return [last7, new Date(today)];
      }

      case 'last30days': {
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 30);
        return [last30, new Date(today)];
      }

      case 'thisMonth': {
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return [thisMonthStart, new Date(today)];
      }

      case 'lastMonth': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return [lastMonthStart, lastMonthEnd];
      }

      case 'customRange':
      default: {
        // For custom range, return last30days as default
        const defaultStart = new Date(today);
        defaultStart.setDate(today.getDate() - 30);
        return [defaultStart, new Date(today)];
      }
    }
  }

  /**
   * Formats the date range for display in input fields
   * @param dateFrom - Start date
   * @param dateTo - End date
   * @returns Formatted string "MM/DD/YYYY - MM/DD/YYYY"
   */
  formatDateRange(dateFrom: Date | null | undefined, dateTo: Date | null | undefined): string {
    if (!dateFrom || !dateTo) {
      return 'Select Date Range';
    }
    
    const formatDate = (d: Date): string => {
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    };
    
    return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
  }

  /**
   * Converts a date to API format (YYYY-MM-DD)
   * @param date - Date to convert
   * @returns ISO date string (YYYY-MM-DD)
   */
  toApiFormat(date: Date | null | undefined): string | null {
    if (!date) return null;
    return date.toISOString().split('T')[0];
  }

  /**
   * Converts a date to UTC format for 'from' parameter.
   * Normalizes the date to start of local day (00:00:00.000), then converts to UTC.
   * @param date - Date to convert
   * @returns ISO string in UTC format (full datetime)
   */
  toUtcDateFrom(date: Date | null | undefined): string | null {
    if (!date) return null;
    
    // Create a copy to avoid mutating the original
    const localDate = new Date(date);
    // Normalize to start of local day (00:00:00.000)
    localDate.setHours(0, 0, 0, 0);
    // Convert to UTC ISO string
    return localDate.toISOString();
  }

  /**
   * Converts a date to UTC format for 'to' parameter (exclusive range).
   * Takes the selected date and returns the start of the next local day (00:00:00.000) converted to UTC.
   * @param date - Date to convert
   * @returns ISO string in UTC format (full datetime) representing start of next day
   */
  toUtcDateTo(date: Date | null | undefined): string | null {
    if (!date) return null;
    
    // Create a copy to avoid mutating the original
    const nextDay = new Date(date);
    // Add one day
    nextDay.setDate(nextDay.getDate() + 1);
    // Normalize to start of local day (00:00:00.000)
    nextDay.setHours(0, 0, 0, 0);
    // Convert to UTC ISO string
    return nextDay.toISOString();
  }

  /**
   * Gets the default date range (Last 30 Days)
   * @returns DateRangeSelection with default values
   */
  getDefaultDateRange(): DateRangeSelection {
    const [dateFrom, dateTo] = this.calculateDateRange('last30days');
    return {
      option: 'last30days',
      dateFrom,
      dateTo
    };
  }

  /**
   * Parses a date string from query parameters
   * @param dateStr - Date string in ISO format (YYYY-MM-DD)
   * @returns Parsed Date in local timezone or null if invalid
   */
  parseDateFromParam(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    
    // Parse as local date (not UTC) to avoid timezone issues
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const day = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);
    
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Determines which DateRangeOption matches the given date range
   * @param dateFrom - Start date
   * @param dateTo - End date
   * @returns The matching DateRangeOption or 'customRange'
   */
  detectDateRangeOption(dateFrom: Date, dateTo: Date): DateRangeOption {
    const options: DateRangeOption[] = ['today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth'];
    
    for (const option of options) {
      const [calcFrom, calcTo] = this.calculateDateRange(option);
      if (this.isSameDay(dateFrom, calcFrom) && this.isSameDay(dateTo, calcTo)) {
        return option;
      }
    }
    
    return 'customRange';
  }

  /**
   * Checks if two dates are the same day
   * @param date1 - First date
   * @param date2 - Second date
   * @returns true if same day
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }
}


